import * as React from "react";
import {
	createBrowserRouter,
	RouterProvider,
	createRoutesFromElements,
	Route,
	Navigate,
	Outlet,
	useLocation,
} from "react-router-dom";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { LoginPage } from "@/pages/auth/LoginPage";
import { MentorLoginPage } from "@/pages/auth/MentorLoginPage";
import { HomePage } from "@/pages/HomePage";
import { ReflectionFlow } from "@/pages/ReflectionFlow";
import { ReflectionDetail } from "@/pages/ReflectionDetail";
import { QAStepPage } from "@/pages/ReflectionFlow/pages/QAStepPage";
import { QAProvider } from "@/contexts/QAContext";
import { AllReflections } from "@/pages/AllReflections/index";
import {
	CheckInIntroPage,
	CheckInWelcomePage,
	CheckInFormPage,
	CheckInCompletePage,
} from "@/pages/DailyCheckIn/pages";
import { AllCheckIns } from "@/pages/AllCheckIns/index";
import { EditCheckIn } from "@/pages/EditCheckIn";
import { AllCorrectiveActions } from "@/pages/AllCorrectiveActions/index";
import { MentorDashboard } from "@/pages/MentorDashboard";
import { MenteeDetail } from "@/pages/MenteeDetail";
import { MentorReflectionDetail } from "@/pages/MentorReflectionDetail";
import { TopicDetailPage } from "@/pages/TopicDetailPage";
import TopicSetupPage from "@/pages/TopicSetup";
import ReflectionTopics from "@/pages/ReflectionTopics";
import MyMentees from "@/pages/MyMentees";
import MyGroups from "@/pages/MyGroups";
import GroupDetail from "@/pages/GroupDetail";
import GroupMembers from "@/pages/GroupMembers";
import { InviteUserPage } from "@/pages/InviteUserPage";
import { AdminPanel } from "@/pages/AdminPanel";
import { AdminRoute } from "@/components/AdminRoute";

import { Layout } from "@/components/Layout";
import { MentorInvitationDialog } from "@/components/MentorInvitationDialog";
import { DateValidator } from "@/components/DateValidator";
import { InactivityWarning } from "@/components/InactivityWarning";
import { TermsConsentGate } from "@/features/terms/TermsConsentGate";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { useTrackUser, usePageTracking } from "@/lib/tracking.ts";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SetPasswordPage } from "@/pages/auth/SetPasswordPage";

function ProtectedRoute({
	children,
	requiredUserType,
}: {
	children: React.ReactNode;
	requiredUserType?: "mentee" | "mentor";
}) {
	const { user, profile, loading } = useAuth();
	const location = useLocation();

	useTrackUser(profile);
	usePageTracking();

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" />
			</div>
		);
	}

	if (!user) {
		const redirectTo = location.pathname + location.search;
		return (
			<Navigate
				to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
				replace
			/>
		);
	}

	if (requiredUserType && profile && profile.user_type !== requiredUserType) {
		if (profile.user_type === "mentor")
			return <Navigate to="/mentor" replace />;
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
}

function LoginGate({ children }: { children: React.ReactNode }) {
	const { user, profile } = useAuth();
	const location = useLocation();
	if (!user) return <>{children}</>;

	// Redirect mentors to mentor area (already logged in)
	if (profile && profile.user_type === "mentor") {
		return <Navigate to="/mentor" replace />;
	}

	const params = new URLSearchParams(location.search);
	const redirect = params.get("redirect");
	const target =
		redirect && redirect.startsWith("/") && !redirect.startsWith("/login")
			? redirect
			: "/";
	return <Navigate to={target} replace />;
}

function MentorLoginGate() {
	const { user } = useAuth();
	const location = useLocation();
	const isRecovery =
		new URLSearchParams(location.search).get("type") === "recovery" ||
		new URLSearchParams((location.hash || "").replace(/^#/, "")).get("type") ===
			"recovery";
	if (isRecovery) return <MentorLoginPage />;
	return user ? <Navigate to="/mentor" replace /> : <MentorLoginPage />;
}

function NotFoundRedirect() {
	const { profile } = useAuth();
	return profile?.user_type === "mentor" ? (
		<Navigate to="/mentor" replace />
	) : (
		<Navigate to="/" replace />
	);
}

function RootShell() {
	return (
		<div className="min-h-screen bg-background">
			<Outlet />
		</div>
	);
}

function AuthenticatedShell() {
	return (
		<>
			<Outlet />
			<MentorInvitationDialog />
			<InactivityWarning />
			<DateValidator />
			<TermsConsentGate />
		</>
	);
}

// Layout wrapper components
function MenteeLayout({ children }: { children: React.ReactNode }) {
	return (
		<ProtectedRoute requiredUserType="mentee">
			<Layout>{children}</Layout>
		</ProtectedRoute>
	);
}

function MentorLayout({ children }: { children: React.ReactNode }) {
	return (
		<ProtectedRoute requiredUserType="mentor">
			<Layout>
				<ErrorBoundary>{children}</ErrorBoundary>
			</Layout>
		</ProtectedRoute>
	);
}

const router = createBrowserRouter(
	createRoutesFromElements(
		<Route element={<RootShell />}>
			{/* Auth routes - no global components */}
			<Route>
				<Route path="/auth/reset-password" element={<ResetPasswordPage />} />
				<Route path="/auth/set-password" element={<SetPasswordPage />} />
				<Route
					path="/login"
					element={
						<LoginGate>
							<LoginPage />
						</LoginGate>
					}
				/>
				<Route path="/mentor-login" element={<MentorLoginGate />} />
			</Route>

			{/* Authenticated routes - with global components */}
			<Route element={<AuthenticatedShell />}>
				{/* Home route */}
				<Route
					path="/"
					element={
						<ProtectedRoute requiredUserType="mentee">
							<Layout>
								<HomePage />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* Mentee routes */}
				<Route
					element={
						<MenteeLayout>
							<Outlet />
						</MenteeLayout>
					}
				>
					<Route path="/reflection/new" element={<ReflectionFlow />} />
					<Route path="/reflection/continue/:id" element={<ReflectionFlow />} />
					<Route path="/reflections" element={<AllReflections />} />
					<Route path="/reflection/:id" element={<ReflectionDetail />} />
					<Route path="/daily-checkin" element={<CheckInIntroPage />} />
					<Route
						path="/daily-checkin/welcome"
						element={<CheckInWelcomePage />}
					/>
					<Route path="/daily-checkin/form" element={<CheckInFormPage />} />
					<Route
						path="/daily-checkin/complete"
						element={<CheckInCompletePage />}
					/>
					<Route path="/check-ins" element={<AllCheckIns />} />
					<Route path="/check-in/edit/:id" element={<EditCheckIn />} />
					<Route
						path="/corrective-actions"
						element={<AllCorrectiveActions />}
					/>
				</Route>

				{/* QA routes (with QAProvider) */}
				<Route
					element={
						<MenteeLayout>
							<QAProvider>
								<Outlet />
							</QAProvider>
						</MenteeLayout>
					}
				>
					<Route path="/reflection/new/qa" element={<QAStepPage />} />
					<Route path="/reflection/:reflectionId/qa" element={<QAStepPage />} />
				</Route>

				{/* Mentor routes */}
				<Route
					element={
						<MentorLayout>
							<Outlet />
						</MentorLayout>
					}
				>
					<Route path="/mentor" element={<MentorDashboard />} />
					<Route path="/mentee/:menteeId" element={<MenteeDetail />} />
					<Route
						path="/mentor/reflection/:reflectionId"
						element={<MentorReflectionDetail />}
					/>
					<Route path="/mentor/my-mentees" element={<MyMentees />} />
					<Route path="/mentor/my-groups" element={<MyGroups />} />
					<Route path="/mentor/topics" element={<ReflectionTopics />} />
					<Route path="/mentor/group/:groupId" element={<GroupDetail />} />
					<Route
						path="/mentor/group/:groupId/members"
						element={<GroupMembers />}
					/>
					<Route path="/mentor/topic/:topicId" element={<TopicDetailPage />} />
					<Route
						path="/mentor/group/:groupId/topic/:topicId"
						element={<TopicDetailPage />}
					/>
					<Route path="/mentor/topic/new" element={<TopicSetupPage />} />
					<Route
						path="/mentor/topic/:topicId/edit"
						element={<TopicSetupPage />}
					/>
				</Route>

				{/* Admin routes */}
				<Route
					path="/admin"
					element={
						<ProtectedRoute>
							<AdminRoute />
						</ProtectedRoute>
					}
				>
					<Route
						index
						element={
							<Layout>
								<AdminPanel />
							</Layout>
						}
					/>
					<Route
						path="invite"
						element={
							<Layout>
								<InviteUserPage />
							</Layout>
						}
					/>
				</Route>

				<Route path="*" element={<NotFoundRedirect />} />
			</Route>
		</Route>,
	),
	{
		future: {
			v7_relativeSplatPath: true,
		},
	},
);

function App() {
	return (
		<AuthProvider>
			<RouterProvider router={router} future={{ v7_startTransition: true }} />
		</AuthProvider>
	);
}

export default App;
